import type { IPiiCustomEntityLabel } from "../types/pii";

// NOTE: The prefix the server gives every custom entity's placeholder type.
const CUSTOM_ENTITY_TYPE_PREFIX = "CUSTOM_";

export interface IResolveCustomPiiEntityNameProps {
  entityType: string;
  // NOTE: Placeholder type → entity name, from the labels endpoint; null while
  // it has not answered, or where the viewer's organization is not the
  // message's (the admin chat viewer).
  customEntityNames: ReadonlyMap<string, string> | null;
}

export const buildCustomPiiEntityNameMap = (
  labels: readonly IPiiCustomEntityLabel[] | undefined,
): ReadonlyMap<string, string> =>
  new Map((labels ?? []).map((label) => [label.entityType, label.name]));

// NOTE: NSAI-6787 — what a chip or a summary calls a custom entity's
// placeholder type: the entity's own name when it is known, else the type
// spelled as words ("CUSTOM_EMPLOYEE_ID" → "Employee id"), so nothing shows
// the raw identifier. Null for a type that is not a custom entity's, which the
// caller labels from its own i18n keys.
export const resolveCustomPiiEntityName = ({
  entityType,
  customEntityNames,
}: IResolveCustomPiiEntityNameProps): string | null => {
  if (!entityType.startsWith(CUSTOM_ENTITY_TYPE_PREFIX)) return null;

  const name = customEntityNames?.get(entityType);
  if (name !== undefined) return name;

  const words = entityType
    .slice(CUSTOM_ENTITY_TYPE_PREFIX.length)
    .toLowerCase()
    .replace(/_+/g, " ")
    .trim();
  return words.length > 0
    ? `${words.charAt(0).toUpperCase()}${words.slice(1)}`
    : null;
};
